/* eslint-disable no-useless-escape */
import Cookies from 'cookies-js';
import decode from 'jwt-decode';
import _ from 'lodash';

const permissions = {
  Dashboard: 'CONSOLE',
  DashboardSinglePatient: 'CONSOLE_SINGLE_PATIENT',
  Coach: 'COACH',
  CoachSupervisor: 'COACH_MANAGEMENT',
  MFA: 'MFA_VERIFIED',
  ClinicalPortal: 'CLINICAL_PORTAL'
};

let userPermissions = [];
let userToken = '';

const TOKEN_IDENTITY = 'userid';
const TOKEN_LIVONGO_UUID = 'bh.uuid';
const COOKIE_NAME = 'token';

const getTokenIdentity = (userToken = '') => {
  const token = userToken || getToken();

  if (!token) {
    return '';
  }

  const parsed = JSON.parse(window.atob(token.split('.')[1]));

  return parsed[TOKEN_IDENTITY];
};

const getTokenLivongoUuid = (userToken = '') => {
  const token = userToken || getToken();
  if (!token) {
    return '';
  }
  const parsed = JSON.parse(window.atob(token.split('.')[1]));
  return parsed[TOKEN_LIVONGO_UUID];
};

const isClinicalPortal = (inputPermissions = []) => {
  const permissionsToTest = !_.isEmpty(inputPermissions)
    ? inputPermissions
    : userPermissions;

  return (
    !_.isEmpty(permissionsToTest) &&
    permissionsToTest.indexOf(permissions.ClinicalPortal) > -1
  );
};

export default {
  logout() {
    window.location = getLogoutUrl();
  },
  returnToLogin() {
    window.location = getLoginUrl();
  },
  requestAuthCode() {
    window.location = getAuthCodeUrl();
  },
  isLoggedIn,
  isVerified,
  hasPermission,
  isDashboardUser,
  isCoach,
  isCoachSupervisor,
  getAppUrl,
  getDomainPrefix,
  getWebuiUrl,
  getApiUrl,
  setToken,
  setReauthToken,
  getToken,
  getReauthToken,
  getPermissions,
  setPermissions,
  clearToken,
  permission: permissions,
  getTokenIdentity,
  getTokenLivongoUuid,
  isClinicalPortal
};

// split the first group on the dash
const nonProdPrefixRegex = /(.*-)+(.*\.).*\.[^:]*:?[0-9]*/;

function isLoggedIn() {
  return getToken() !== null;
}

function isVerified() {
  return (
    !_.isEmpty(userPermissions) &&
    userPermissions.indexOf(permissions.MFA) !== -1
  );
}

// Returns true if user's given permissions match any of the allowed permissions
function hasPermission(allowedPermissions) {
  if (!allowedPermissions) {
    return true;
  }
  return userPermissions.some(p => allowedPermissions.indexOf(p) > -1);
}

function isDashboardUser() {
  return (
    !_.isEmpty(userPermissions) &&
    userPermissions.indexOf(permissions.Dashboard) > -1
  );
}

function isCoach() {
  return (
    !_.isEmpty(userPermissions) &&
    userPermissions.indexOf(permissions.Coach) > -1
  );
}

function isCoachSupervisor() {
  return (
    !_.isEmpty(userPermissions) &&
    userPermissions.indexOf(permissions.CoachSupervisor) > -1
  );
}

function getTokenExpirationDate(encodedToken) {
  const token = decode(encodedToken);
  if (!token.exp) {
    return null;
  }

  const date = new Date(0);
  date.setUTCSeconds(token.exp);

  return date;
}

function isTokenExpired(token) {
  const expirationDate = getTokenExpirationDate(token);
  return expirationDate < new Date();
}

/**
 *
 * TODO: Update the Cookie Library so SameSite='None' can be passed as option.
 * Remove: _setCookieInternal, _getExpiresDate and _generateCookieString
 *
 */
const _generateCookieString = function(key, value, options) {
  key = key.replace(/[^#$&+\^`|]/g, encodeURIComponent);
  key = key.replace(/\(/g, '%28').replace(/\)/g, '%29');
  value = (value + '').replace(/[^!#$&-+\--:<-\[\]-~]/g, encodeURIComponent);
  options = options || {};

  let cookieString = key + '=' + value;
  cookieString += options.path ? ';path=' + options.path : '';
  cookieString += options.domain ? ';domain=' + options.domain : '';
  cookieString += options.expires
    ? ';expires=' + options.expires.toUTCString()
    : '';

  if (options.secure) {
    cookieString += ';secure';

    // Only set SameSite if secure, for local dev
    cookieString += options.sameSite
      ? ';SameSite=' + options.sameSite
      : ';SameSite=None';
  }

  return cookieString;
};

const _getExpiresDate = function(expires, now) {
  now = now || new Date();

  if (typeof expires === 'number') {
    expires =
      expires === Infinity
        ? Cookies._maxExpireDate
        : new Date(now.getTime() + expires * 1000);
  } else if (typeof expires === 'string') {
    expires = new Date(expires);
  }

  if (expires && !Cookies._isValidDate(expires)) {
    throw new Error(
      '`expires` parameter cannot be converted to a valid Date instance'
    );
  }

  return expires;
};

const _setCookieInternal = function(key, value, options) {
  options.expires = _getExpiresDate(value === undefined ? -1 : options.expires);
  window.document.cookie = _generateCookieString(key, value, options);
};

function setToken(token, isClinicalPortal) {
  const domain = getCookieDomain();
  // This is required for SameSite to work, in the Clinical Portal, iframe
  // set this in both UAT and prod.
  const secure = domain !== 'localhost' || isClinicalPortal;
  const expires = getTokenExpirationDate(token);

  userToken = token;

  if (!isClinicalPortal) {
    _setCookieInternal(COOKIE_NAME, token, {
      domain,
      path: '/',
      expires,
      secure
    });
  }

  setPermissions();
}

function setReauthToken(token) {
  const domain = getCookieDomain();
  // This is required for SameSite to work, in the Clinical Portal, iframe
  // set this in both UAT and prod.
  const secure = domain !== 'localhost';

  _setCookieInternal('reauth_token', token, {
    domain,
    path: '/',
    secure
  });
}

function getToken() {
  // Grab the token from our authentication process
  let cookie = userToken || Cookies.get(COOKIE_NAME);
  if (cookie === 'undefined') {
    return null;
  }
  if (cookie && !isTokenExpired(cookie)) {
    return cookie;
  }
  return null;
}

function getReauthToken() {
  // Grab the token from our authentication process
  let cookie = Cookies.get('reauth_token');
  if (cookie) {
    return cookie;
  }
  return null;
}

function setPermissions() {
  const token = getToken();
  userPermissions = JSON.parse(atob(token.split('.')[1])).permissions;
}

function getPermissions() {
  return userPermissions;
}

function clearToken() {
  const domain = getCookieDomain();
  const secure = domain !== 'localhost';
  const cookieOptions = {
    domain: domain,
    path: '/',
    secure: secure
  };
  Cookies.set(COOKIE_NAME, '', cookieOptions);
  Cookies.expire(COOKIE_NAME, cookieOptions);
  Cookies.set('reauth_token', '', cookieOptions);
  Cookies.expire('reauth_token', cookieOptions);

  userToken = '';
}

function getLoginUrl() {
  return getWebuiUrl() + '/login';
}

function getLogoutUrl() {
  return getWebuiUrl() + '/logout';
}

function getAuthCodeUrl() {
  return getWebuiUrl() + '/login/authcode';
}

function getAppUrl() {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:9099';
  }
  return getServiceUrl('app');
}

function getWebuiUrl() {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5050';
  }
  return getServiceUrl('web-ui');
}

function getApiUrl() {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:9080';
  }
  return getServiceUrl('api');
}

function getServiceUrl(service) {
  const serviceUrl =
    window.location.protocol +
    '//' +
    window.location.hostname.replace('dashboard', service);
  const isDevUrl = window.location.hostname === 'dashboard.mystrength.dev';
  return isDevUrl ? `${serviceUrl}:${window.location.port}` : serviceUrl;
}

function getSubDomain() {
  const host = window.location.hostname;
  const hostPieces = host.split('.');
  return hostPieces.slice(1, hostPieces.length).join('.');
}

function getDomainPrefix() {
  const host = window.location.host;
  const matches = nonProdPrefixRegex.exec(host);
  return matches && matches.length === 3 ? matches[2] : '';
}

function getCookieDomain() {
  return window.location.hostname === 'localhost'
    ? 'localhost'
    : getSubDomain();
}
